import StartupPageTitle from '../../../components/StartupPageTitle';
import { Bank as PageHeadingIcon } from '@phosphor-icons/react';
/**
 * CreditApplication.jsx — Iter 69, reworked in Iter 72.
 *
 * Credit is not the services marketplace: there is nothing to browse and pick.
 * Founderboard takes one application to its lending panel, so this screen is a
 * single form, and once it's in, a status page with a thread to our credit team.
 *
 *   no live application  ->  intro, "Start application", + past applications
 *   draft                ->  the five-page wizard
 *   submitted+           ->  acknowledgement + status + thread + past applications
 *
 * (Iter 72) Two things this screen deliberately does NOT do any more:
 *
 *   - It no longer bars the door on the MoM check. A founder can open the
 *     application and fill it in whether or not their six months of monthly
 *     updates are in; the check rides along on every page (MomStrip) and is
 *     re-verified from the server on each save, but it only *blocks* at submit.
 *     Locking someone out of a form they can't see is worse than letting them
 *     fill it and telling them, on every page, what's still outstanding.
 *   - It's no longer amnesiac. Past applications are listed with their status
 *     and a progress ring, the same way the services Engagements tab lists
 *     engagements, so a declined or closed application and its thread stay
 *     reachable.
 *
 * The server owns every gate. `mom.ok`, `data_missing` and the required-document
 * list all come back from the API and are re-checked on submit, so a disabled
 * button here is a courtesy, not the control.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { useCurrency } from '@/hooks/useCurrency';
import { formatDate } from '@/utils/datetime';
import {
  ArrowLeft, ArrowRight, ArrowsClockwise, Bank, CalendarX, CaretDown, Check, CheckCircle, ChatCircleDots,
  Circle, ClockCounterClockwise, FileText, ShieldCheck, Spinner, UploadSimple, Warning, X,
} from '@phosphor-icons/react';
import ServiceChat from '@/components/ServiceChat';
import ProgressDonut from '@/components/ProgressDonut';

const ACCENT = '#f59e0b';           // credit amber, matching the marketplace
const CHAT_BASE = '/credit/applications';

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ym = (m) => `${MONTHS[m.month]} ${m.year}`;

const STATUS_META = {
  draft:          { label: 'Draft',           tone: 'muted',   blurb: 'Not submitted yet — pick it up any time.' },
  submitted:      { label: 'Received',        tone: 'info',    blurb: 'In the queue with our credit team.' },
  under_review:   { label: 'Under review',    tone: 'info',    blurb: 'A reviewer is working through your numbers.' },
  info_requested: { label: 'Needs your input', tone: 'warning', blurb: 'We’ve asked you something on the thread.' },
  approved:       { label: 'Offers ready',    tone: 'success', blurb: 'We have something for you — check the thread.' },
  declined:       { label: 'Not proceeding',  tone: 'muted',   blurb: 'We couldn’t place this one. The thread has the detail.' },
  closed:         { label: 'Closed',          tone: 'muted',   blurb: 'This application is closed.' },
};
const TONES = {
  info:    'bg-info/15 text-info',
  warning: 'bg-warning/15 text-warning',
  success: 'bg-success/15 text-success',
  muted:   'bg-text-muted/15 text-text-muted',
};
const SUCCESS = '#22c55e';   // literal: --success is a Tailwind token, not a CSS var

/**
 * `openApplication` — the /startup/credit/application route, which the
 * notification emails deep-link to, skips the landing and goes straight to the
 * application. Every other entry lands on CreditHome.
 */
const CreditApplication = ({ openApplication = false }) => {
  const [form, setForm] = useState(null);       // /credit/application-form
  const [app, setApp] = useState(null);         // the live application, if any
  const [history, setHistory] = useState([]);   // every application, newest first
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  // (Iter 72) 'home' | 'app'. The credit tab now ALWAYS opens on the home
  // screen — the MoM check, the apply button and the application list — rather
  // than dropping a founder with work in progress straight into the wizard.
  // Landing on page 3 of a form you last touched a fortnight ago tells you
  // nothing about where your application stands, and hid the whole screen from
  // anyone who had ever started one.
  const [view, setView] = useState(openApplication ? 'app' : 'home');

  const load = useCallback(async () => {
    try {
      // Fetched together rather than lazily: the home screen needs both the MoM
      // check and the application list, and staggering them makes it pop in.
      const [f, mine] = await Promise.all([
        api.get('/credit/application-form'),
        api.get('/credit/applications/mine'),
      ]);
      setForm(f.data);
      setHistory(mine.data.applications || []);
      if (f.data.existing_application_id) {
        const a = await api.get(`/credit/applications/${f.data.existing_application_id}`);
        setApp(a.data.application);
      } else {
        setApp(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the credit application');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post('/credit/applications', { credit_types: [] });
      setApp(r.data.application);
      if (r.data.resumed) toast.info('Picking up your existing application.');
      setView('app');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start an application'); }
    finally { setStarting(false); }
  };

  const home = () => { setView('home'); load(); };

  if (loading) return <div className="p-6 text-sm text-text-muted" data-testid="credit-app-loading">Loading…</div>;

  if (view === 'app' && app) {
    // Submitted or beyond → the acknowledgement + thread, never the form again.
    return app.submitted_at
      ? <SubmittedView app={app} past={history.filter((h) => h.id !== app.id)}
          onRefresh={load} onHome={home} />
      : <ApplicationForm app={app} form={form} onSubmitted={load} onHome={home} />;
  }
  return (
    <CreditHome form={form} app={app} history={history} onStart={start} starting={starting}
      onOpen={() => setView('app')} onRefresh={load} />
  );
};

/* ---------------------------------------------------------------- home ----- */

/**
 * The credit tab's home screen — what a founder sees on every login.
 *
 * Three things, in this order, because they answer the three questions a founder
 * actually arrives with:
 *
 *   1. Am I eligible?      the 6-month MoM check, present or not, month by month
 *   2. What do I do next?  one button — start, resume, or open
 *   3. Where do I stand?   every application with its status and progress
 *
 * The "how it works" explainer only renders for a founder who has never applied.
 * For everyone else it's the same three paragraphs between them and their file.
 */
const CreditHome = ({ form, app, history, onStart, starting, onOpen, onRefresh }) => {
  const mom = form?.mom || {};
  const ready = !!mom.ok;
  const firstTime = !history.length;
  const draft = app && !app.submitted_at;
  const step = draft
    ? Math.max(1, (app.steps || []).findIndex((s) => s.key === (app.form_step || 'types')) + 1)
    : 0;

  return (
    <div className="p-4 sm:p-6" data-testid="credit-app-home">
      <div className="max-w-3xl mx-auto">
        <Hero />

        {/* 1 — eligibility. Checked live on every visit, not cached from signup. */}
        <MomGate mom={mom} />

        {/* 2 — the one action. Wording follows the state, so the button never
            lies about what pressing it will do. */}
        {app ? (
          <button onClick={onOpen} data-testid="credit-app-open-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white"
            style={{ backgroundColor: ACCENT }}>
            <Bank size={14} weight="bold" />
            {draft
              ? `Resume your application — page ${step} of ${(app.steps || []).length || 5}`
              : 'Open your application'}
            <ArrowRight size={13} weight="bold" />
          </button>
        ) : (
          <button onClick={onStart} disabled={starting} data-testid="credit-app-start-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}>
            {starting ? 'Starting…' : ready ? 'Apply for credit' : 'Apply for credit anyway'}
            {!starting && <ArrowRight size={13} weight="bold" />}
          </button>
        )}
        <p className="text-[11px] text-text-muted mt-2 text-center">
          {app
            ? draft
              ? 'Nothing is submitted until you reach the last page. Everything you type is saved.'
              : `We reply within ${app.sla_hours || 48} hours — the thread is on your application.`
            : ready
              ? 'Five short pages. Everything saves as you go, so you can stop and come back.'
              : <>You can fill the whole form now &mdash; we&rsquo;ll only need the missing months before you submit it.</>}
        </p>

        {/* 3 — where every application stands, current one included. */}
        <CreditHistory apps={history} currentId={app?.id} onRefresh={onRefresh} onOpen={onOpen} />

        {firstTime && (
          <Panel testid="credit-app-how">
            <SectionTitle sub="One application, not six.">How Founderboard credit works</SectionTitle>
            <ol className="space-y-2.5 mt-1">
              {[
                ['Tell us what you need', 'One form: your entity, your numbers, the facility you’re after, and the documents a lender will ask for.'],
                ['We take it to our panel', 'We shop it to the banks, NBFCs and debt funds whose products actually fit your stage — rather than you filling in six portals.'],
                [`We come back within ${form?.sla_hours || 48} hours`, 'A reviewer reads it and replies on a thread right here. You can ask us anything in the meantime.'],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full flex-shrink-0 inline-flex items-center justify-center text-[11px] font-mono font-bold"
                    style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}>{i + 1}</span>
                  <div>
                    <div className="text-[13.5px] font-semibold text-text-primary">{t}</div>
                    <div className="text-[12px] text-text-secondary leading-snug">{d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Panel>
        )}
      </div>
    </div>
  );
};

/** Back to the home screen, on both the wizard and the submitted view. */
const HomeLink = ({ onHome }) => (
  <button onClick={onHome} data-testid="credit-app-home-link"
    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-text-muted hover:text-text-primary mb-3">
    <ArrowLeft size={11} weight="bold" /> Credit home
  </button>
);

const Hero = () => (
  <div className="mb-5 startup-page-header">
    <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: ACCENT }}>Founderboard Credit</div>
    <StartupPageTitle icon={PageHeadingIcon} className="text-2xl sm:text-3xl font-heading font-bold text-text-primary leading-[1.05]" style={{ letterSpacing: '-0.02em' }}>
      Apply for credit
    </StartupPageTitle>
    <p className="text-sm text-text-secondary mt-1.5 max-w-2xl">
      Debt that fits your stage — working capital, revenue-based finance, invoice discounting and more.
      We underwrite the introduction ourselves, so you deal with us, not a call centre.
    </p>
  </div>
);

/**
 * The 6-month MoM gate. Shows exactly which months are missing, because
 * "ineligible" without a list is a dead end for the founder.
 */
const MomGate = ({ mom }) => {
  const ok = !!mom.ok;
  const missing = mom.missing || [];
  const present = (mom.present || []).length;
  const required = mom.required || 6;
  return (
    <div className={`rounded-xl border p-4 ${ok ? 'border-success/40 bg-success/10' : 'border-warning/40 bg-warning/10'}`}
      data-testid="credit-app-mom-gate">
      <div className="flex items-start gap-3">
        {ok
          ? <CheckCircle size={18} weight="fill" className="text-success flex-shrink-0 mt-0.5" />
          : <CalendarX size={18} weight="fill" className="text-warning flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-text-primary">
            {ok
              ? `${required} months of monthly updates on file — you're good to apply`
              : `${present} of ${required} months of monthly updates on file`}
          </div>
          <p className="text-[12px] text-text-secondary leading-relaxed mt-0.5">
            {ok
              ? 'Lenders price off the trend, and yours is complete for the last six months.'
              : 'Credit review needs an unbroken run of the last six complete months. Fill the gaps below and this unlocks automatically.'}
          </p>

          {/* Month strip — the whole window, so present and missing read together. */}
          <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="credit-app-mom-months">
            {(mom.window || []).map((m) => {
              const isMissing = missing.some((x) => x.year === m.year && x.month === m.month);
              return (
                <span key={`${m.year}-${m.month}`}
                  className={`text-[11px] font-semibold px-2 py-1 rounded inline-flex items-center gap-1 ${
                    isMissing ? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'}`}>
                  {isMissing ? <X size={9} weight="bold" /> : <Check size={9} weight="bold" />} {ym(m)}
                </span>
              );
            })}
          </div>

          {!ok && (
            <Link to="/startup/update" data-testid="credit-app-mom-cta"
              className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white"
              style={{ backgroundColor: ACCENT }}>
              Fill monthly updates <ArrowRight size={10} weight="bold" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * (Iter 72) The same check as MomGate, but slim enough to sit on every page of
 * the wizard.
 *
 * It's on all five pages rather than just the last one because the founder is
 * no longer stopped at the door: they can fill the whole form with months
 * missing, so the outstanding requirement has to stay in front of them the whole
 * way instead of ambushing them at submit. `Re-check` re-reads the application —
 * a founder who goes and files the gap in another tab shouldn't have to reload.
 */
const MomStrip = ({ mom, onRecheck, busy, blocking }) => {
  const ok = !!mom.ok;
  const missing = mom.missing || [];
  const present = (mom.present || []).length;
  const required = mom.required || 6;
  return (
    <div className={`rounded-lg border px-3 py-2 mb-4 flex items-center gap-2.5 flex-wrap ${
      ok ? 'border-success/35 bg-success/5' : 'border-warning/35 bg-warning/5'}`}
      data-testid="credit-app-mom-strip">
      {ok
        ? <CheckCircle size={15} weight="fill" className="text-success flex-shrink-0" />
        : <CalendarX size={15} weight="fill" className="text-warning flex-shrink-0" />}
      <span className="text-[12px] font-semibold text-text-primary">
        {ok ? `Monthly updates verified · ${required}/${required}` : `Monthly updates ${present}/${required}`}
      </span>

      {/* Only the gaps are named here — listing six green chips on every page is
          noise once the check passes. */}
      {!ok && (
        <span className="flex flex-wrap gap-1" data-testid="credit-app-mom-strip-missing">
          {missing.map((m) => (
            <span key={`${m.year}-${m.month}`}
              className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-warning/20 text-warning">
              {ym(m)}
            </span>
          ))}
        </span>
      )}

      <span className="text-[11px] text-text-muted">
        {ok ? 'Required for submission.'
          : blocking ? 'This is what’s holding up submission.'
          : 'Needed to submit — not to keep filling this in.'}
      </span>

      <div className="flex-1" />
      {!ok && (
        <Link to="/startup/update" data-testid="credit-app-mom-strip-cta"
          className="text-[11px] font-semibold px-2 py-1 rounded inline-flex items-center gap-1 text-white flex-shrink-0"
          style={{ backgroundColor: ACCENT }}>
          Fill them <ArrowRight size={9} weight="bold" />
        </Link>
      )}
      <button type="button" onClick={onRecheck} disabled={busy}
        data-testid="credit-app-mom-recheck"
        className="text-[11px] font-semibold text-text-muted hover:text-text-primary inline-flex items-center gap-1 disabled:opacity-40 flex-shrink-0">
        <ArrowsClockwise size={10} weight="bold" className={busy ? 'animate-spin' : ''} /> Re-check
      </button>
    </div>
  );
};

/* -------------------------------------------------------------- wizard ----- */

/**
 * The application, as five pages.
 *
 *   1 types       what kind of credit
 *   2 company     entity + contact sections
 *   3 financials  financial position + the ask
 *   4 documents   the vault checklist
 *   5 consent     data-sharing consent, then submit
 *
 * Every forward move writes to the server — the form values, the selected
 * credit types AND the page the founder is on — so closing the tab mid-way and
 * coming back resumes on the same page with the same answers. The step list,
 * which sections belong to which page, and whether a page is complete all come
 * from the server (`app.steps`), so the rail's ticks and the submit gate can't
 * disagree.
 */
const ApplicationForm = ({ app: initialApp, form, onSubmitted, onHome }) => {
  const { format } = useCurrency();
  // The application is held locally and replaced from each PATCH response, so
  // server-computed step completeness refreshes as the founder types + saves.
  const [app, setApp] = useState(initialApp);
  const [values, setValues] = useState(() => ({ ...(form?.prefill || {}), ...(initialApp.data || {}) }));
  const [types, setTypes] = useState(() => initialApp.credit_types || []);
  const [step, setStep] = useState(() => initialApp.form_step || 'types');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState('');
  const [showConsent, setShowConsent] = useState(false);
  const [rechecking, setRechecking] = useState(false);

  // Memoised: `current` and then `liveBlockers` hang off this, so a fresh []
  // fallback each render would recompute the blocker list on every keystroke.
  const steps = useMemo(() => app.steps || [], [app.steps]);
  const docs = app.documents || [];
  const mom = app.mom || {};
  const consentDoc = app.consent || {};
  const products = form?.credit_products || [];
  const fields = useMemo(() => app.data_fields || [], [app.data_fields]);
  const sections = useMemo(() => app.field_sections || [], [app.field_sections]);

  const idx = Math.max(0, steps.findIndex((s) => s.key === step));
  // Memoised: `liveBlockers` below depends on it, and a fresh {} fallback each
  // render would recompute that on every keystroke.
  const current = useMemo(() => steps[idx] || {}, [steps, idx]);
  const isLast = idx === steps.length - 1;

  const setField = (id, v) => setValues((p) => ({ ...p, [id]: v }));

  /** Write values + types + the page we're on. Returns the fresh application. */
  const persist = async (nextStep, { quiet = true } = {}) => {
    setSaving(true);
    try {
      const payload = { ...values };
      delete payload.updated_at;
      const r = await api.patch(`/credit/applications/${app.id}`, {
        data: payload,
        credit_types: types,
        form_step: nextStep || step,
      });
      setApp(r.data.application);
      if (!quiet) toast.success('Saved — you can pick this up later.');
      return r.data.application;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
      return null;
    } finally { setSaving(false); }
  };

  const goTo = async (key) => {
    const fresh = await persist(key);
    if (fresh) setStep(key);
  };

  /** Re-read the application — picks up monthly updates or vault files added in
   *  another tab without discarding what's typed on this page. */
  const recheck = async () => {
    setRechecking(true);
    try {
      const r = await api.get(`/credit/applications/${app.id}`);
      setApp(r.data.application);
      toast[r.data.application.mom?.ok ? 'success' : 'info'](
        r.data.application.mom?.ok
          ? 'Monthly updates are complete — you can submit once the rest is filled.'
          : `Still missing ${(r.data.application.mom?.missing || []).length} month(s) of monthly updates.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not re-check'); }
    finally { setRechecking(false); }
  };
  const next = () => goTo(steps[Math.min(idx + 1, steps.length - 1)]?.key || step);
  const back = () => goTo(steps[Math.max(idx - 1, 0)]?.key || step);

  // Uploading re-reads the application so the documents step's ticks and the
  // rail update together.
  const uploadToSlot = async (doc, file) => {
    if (!file) return;
    const key = `${doc.dataroom_id}::${doc.slot_id}`;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post('/files/upload', fd);
      await api.post('/vault/files', {
        dataroom_id: doc.dataroom_id, slot_id: doc.slot_id,
        file_id: up.data.file_id, filename: up.data.filename,
      });
      toast.success(`${up.data.filename} added to your vault.`);
      const r = await api.get(`/credit/applications/${app.id}`);
      setApp(r.data.application);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(''); }
  };

  const submit = async () => {
    if (!(await persist('consent'))) return;
    setSubmitting(true);
    try {
      await api.post(`/credit/applications/${app.id}/submit`, { consent_accepted: consent });
      toast.success('Application submitted.');
      onSubmitted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit'); }
    finally { setSubmitting(false); }
  };

  // The rail reflects the last SAVED state. Unsaved edits on the page in front
  // of the founder are folded in so the tick doesn't lag a keystroke behind.
  const liveBlockers = useMemo(() => {
    if (current.key === 'types') return types.length === 0 ? ['Pick at least one type of credit'] : [];
    if (current.key === 'consent') return consent ? (current.blockers || []) : [...(current.blockers || []), 'Tick the consent box'];
    if ((current.sections || []).length) {
      return fields
        .filter((f) => f.required && current.sections.includes(f.section)
          && String(values[f.id] ?? '').trim() === '')
        .map((f) => `${f.label} is required`);
    }
    return current.blockers || [];
  }, [current, types, consent, fields, values]);

  const amount = Number(values.amount_sought_inr || 0);

  return (
    <div className="p-4 sm:p-6" data-testid="credit-app-form">
      <div className="max-w-3xl mx-auto">
        <HomeLink onHome={onHome} />
        <Hero />

        <StepRail steps={steps} activeIdx={idx} onPick={goTo} busy={saving} />

        {/* (Iter 72) On every page, not just the last one. */}
        <MomStrip mom={mom} onRecheck={recheck} busy={rechecking} blocking={isLast} />

        <Panel testid={`credit-app-step-${current.key}`}>
          <SectionTitle sub={current.blurb}>
            {current.title} {idx === 0 && <Req />}
          </SectionTitle>

          {/* 1 — credit types, from the approved credit catalog. */}
          {current.key === 'types' && (
            products.length === 0 ? (
              <div className="text-[12px] text-text-muted">No credit products are configured yet.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[26rem] overflow-y-auto pr-1">
                  {products.map((p) => {
                    const on = types.includes(p.id);
                    return (
                      <button key={p.id} type="button"
                        onClick={() => setTypes((prev) => on ? prev.filter((t) => t !== p.id) : [...prev, p.id])}
                        data-testid={`credit-app-type-${p.id}`}
                        className={`text-left rounded-lg border p-2.5 transition-colors ${on ? '' : 'border-border hover:border-text-primary'}`}
                        style={on ? { borderColor: ACCENT, backgroundColor: `${ACCENT}12` } : {}}>
                        <div className="flex items-start gap-2">
                          <span className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 inline-flex items-center justify-center border ${on ? '' : 'border-border'}`}
                            style={on ? { backgroundColor: ACCENT, borderColor: ACCENT } : {}}>
                            {on && <Check size={9} weight="bold" className="text-white" />}
                          </span>
                          <div className="min-w-0">
                            <div className="text-[12.5px] font-semibold text-text-primary leading-tight">{p.name}</div>
                            {p.description && <div className="text-[11px] text-text-muted leading-snug mt-0.5 line-clamp-2">{p.description}</div>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-text-muted mt-2">
                  {types.length} selected{types.length === 0 && ' — pick at least one'}
                </div>
              </>
            )
          )}

          {/* 2 & 3 — the field sections this page owns. */}
          {(current.sections || []).length > 0 && (
            <div className="space-y-5">
              {sections.filter((s) => current.sections.includes(s.id)).map((sec) => (
                <div key={sec.id} data-testid={`credit-app-section-${sec.id}`}>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-text-muted mb-2">{sec.title}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {fields.filter((f) => f.section === sec.id).map((f) => (
                      <CreditField key={f.id} field={f} value={values[f.id]} onChange={(v) => setField(f.id, v)} />
                    ))}
                  </div>
                </div>
              ))}
              {current.sections.includes('ask') && amount > 0 && (
                <div className="text-[12px] text-text-secondary">
                  You&rsquo;re asking for <strong className="font-mono" style={{ color: ACCENT }}>{format(amount, 'INR')}</strong>
                  {values.tenure_months ? <> over {values.tenure_months} months</> : null}.
                </div>
              )}
            </div>
          )}

          {/* 4 — documents. */}
          {current.key === 'documents' && (
            <div className="space-y-2">
              <div className="text-[11px] text-text-muted mb-1">
                {docs.filter((d) => d.filled).length} of {docs.length} on file ·
                {' '}{docs.filter((d) => d.required).length} required
              </div>
              {docs.map((d) => {
                const key = `${d.dataroom_id}::${d.slot_id}`;
                const isUploading = uploading === key;
                const blocking = d.required && !d.filled;
                return (
                  <div key={key}
                    className={`rounded-lg border p-3 ${blocking ? 'border-warning/40 bg-warning/5' : 'border-border bg-background'}`}
                    data-testid={`credit-app-doc-${d.slot_id}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      {d.filled ? (
                        <span className="w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}><Check size={10} weight="bold" /></span>
                      ) : (
                        <Circle size={16} className={blocking ? 'text-warning flex-shrink-0' : 'text-text-muted flex-shrink-0'} />
                      )}
                      <div className="flex-1 min-w-[180px]">
                        <div className="text-[13px] font-medium text-text-primary">
                          {d.slot_name}
                          {d.required
                            ? <span className="text-danger"> *</span>
                            : <span className="text-[10px] text-text-muted font-normal ml-1.5">optional</span>}
                        </div>
                        <div className="text-[11px] text-text-muted leading-snug">{d.why}</div>
                        {d.filled && d.files?.[0] && (
                          <div className="text-[10.5px] mt-0.5" style={{ color: ACCENT }}>
                            <FileText size={9} weight="bold" className="inline mr-1" />{d.files[0].filename}
                          </div>
                        )}
                      </div>
                      <label className={`text-[11.5px] font-semibold px-2.5 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 flex-shrink-0 ${
                        d.filled ? 'border border-border text-text-secondary hover:text-text-primary' : 'text-white'}`}
                        style={d.filled ? {} : { backgroundColor: ACCENT }}>
                        {isUploading ? <Spinner size={11} weight="bold" className="animate-spin" /> : <UploadSimple size={11} weight="bold" />}
                        {isUploading ? 'Uploading…' : d.filled ? 'Replace' : 'Upload'}
                        <input type="file" className="hidden" disabled={isUploading}
                          data-testid={`credit-app-upload-${d.slot_id}`}
                          onChange={(e) => uploadToSlot(d, e.target.files?.[0])} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 5 — consent, then submit. */}
          {current.key === 'consent' && (
            <div>
              {/* No MomGate here — the strip above every page already carries the
                  months and the link, and it says so more strongly on this page. */}
              <div className="rounded-xl border p-3.5"
                style={{ backgroundColor: `${ACCENT}0d`, borderColor: `${ACCENT}44` }}>
                <div className="flex items-start gap-2.5">
                  <ShieldCheck size={16} weight="fill" style={{ color: ACCENT }} className="flex-shrink-0 mt-0.5" />
                  <div className="text-[12px] text-text-secondary leading-relaxed">
                    <p className="mb-2">{consentDoc.intro}</p>
                    {(showConsent ? (consentDoc.clauses || []) : (consentDoc.clauses || []).slice(0, 2)).map((c) => (
                      <div key={c.heading} className="mb-2 last:mb-0">
                        <div className="text-[12px] font-heading font-bold text-text-primary">{c.heading}</div>
                        <p className="mt-0.5" dangerouslySetInnerHTML={{ __html: c.body }} />
                      </div>
                    ))}
                    {(consentDoc.clauses || []).length > 2 && (
                      <button type="button" onClick={() => setShowConsent((s) => !s)}
                        data-testid="credit-app-consent-toggle"
                        className="text-[11.5px] font-semibold mt-1" style={{ color: ACCENT }}>
                        {showConsent ? 'Show less' : `Read all ${consentDoc.clauses.length} points`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer p-3 mt-3 rounded-lg border border-border hover:border-text-primary"
                data-testid="credit-app-consent-label">
                <input type="checkbox" className="mt-0.5" checked={consent}
                  data-testid="credit-app-consent-checkbox"
                  onChange={(e) => setConsent(e.target.checked)} />
                <span className="text-[12.5px] text-text-secondary leading-relaxed">{consentDoc.acknowledgement}</span>
              </label>
              <div className="text-[10px] text-text-muted mt-1.5">
                Version {consentDoc.version} · reference {String(consentDoc.hash || '').slice(0, 12)}
              </div>
            </div>
          )}
        </Panel>

        {/* What this page still wants. Shown per-page rather than as one list at
            the end, so a founder fixes it while they're looking at it. */}
        {liveBlockers.length > 0 && (
          <div className="mt-3 rounded-lg border border-warning/30 bg-warning/5 p-3 text-[11.5px] text-text-secondary"
            data-testid="credit-app-step-blockers">
            <div className="font-semibold text-warning inline-flex items-center gap-1.5 mb-1">
              <Warning size={12} weight="fill" /> Still needed on this page
            </div>
            <ul className="space-y-0.5 ml-0.5">
              {liveBlockers.map((b) => <li key={b}>· {b}</li>)}
            </ul>
          </div>
        )}

        {/* Navigation. Forward always saves; the founder can also leave from any
            page and the answers plus the page itself are kept. */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <button onClick={back} disabled={idx === 0 || saving || submitting}
            data-testid="credit-app-back"
            className="px-3.5 py-2.5 rounded-lg text-sm font-semibold border border-border text-text-secondary hover:text-text-primary disabled:opacity-40 inline-flex items-center gap-1.5">
            <ArrowLeft size={12} weight="bold" /> Back
          </button>
          <button onClick={() => persist(step, { quiet: false })} disabled={saving || submitting}
            data-testid="credit-app-save"
            className="px-3.5 py-2.5 rounded-lg text-sm font-semibold border border-border text-text-secondary hover:text-text-primary disabled:opacity-40">
            {saving ? 'Saving…' : 'Save & finish later'}
          </button>
          <div className="flex-1" />
          {!isLast ? (
            <button onClick={next} disabled={saving || submitting || liveBlockers.length > 0}
              data-testid="credit-app-next"
              className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 inline-flex items-center gap-1.5"
              style={{ backgroundColor: ACCENT }}>
              {saving ? 'Saving…' : `Next: ${steps[idx + 1]?.label || ''}`} <ArrowRight size={12} weight="bold" />
            </button>
          ) : (
            <button onClick={submit}
              disabled={submitting || saving || !consent || !mom.ok || steps.some((s) => s.key !== 'consent' && !s.complete)}
              data-testid="credit-app-submit"
              className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 inline-flex items-center gap-2"
              style={{ backgroundColor: ACCENT }}>
              <Bank size={13} weight="bold" />
              {submitting ? 'Submitting…' : 'Submit application'}
            </button>
          )}
        </div>

        {/* On the last page, surface anything still outstanding on OTHER pages —
            otherwise the submit button is disabled with no visible reason. */}
        {isLast && steps.some((s) => s.key !== 'consent' && !s.complete) && (
          <div className="mt-2.5 rounded-lg border border-warning/30 bg-warning/5 p-3 text-[11.5px] text-text-secondary"
            data-testid="credit-app-outstanding">
            <div className="font-semibold text-warning mb-1">Earlier pages need finishing</div>
            {steps.filter((s) => s.key !== 'consent' && !s.complete).map((s) => (
              <button key={s.key} onClick={() => goTo(s.key)}
                className="block text-left hover:text-text-primary">
                · <span className="underline">{s.label}</span> — {s.blockers.join(', ')}
              </button>
            ))}
          </div>
        )}

        <p className="text-[11px] text-text-muted mt-3 text-center">
          Your answers are saved on this device&rsquo;s account, not in the browser — you can close this and
          come back to page {idx + 1} any time.
        </p>
      </div>
    </div>
  );
};

/** Five-page rail. Completed pages are revisitable; ticks come from the server. */
const StepRail = ({ steps, activeIdx, onPick, busy }) => (
  <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1" data-testid="credit-app-rail">
    {steps.map((s, i) => {
      const active = i === activeIdx;
      const done = s.complete && !active;
      return (
        <React.Fragment key={s.key}>
          {i > 0 && (
            <span className="h-px w-3 sm:w-6 flex-shrink-0"
              style={{ backgroundColor: steps[i - 1].complete ? ACCENT : 'var(--border)' }} />
          )}
          <button onClick={() => !busy && onPick(s.key)} disabled={busy}
            data-testid={`credit-app-rail-${s.key}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold whitespace-nowrap transition-colors ${
              active ? 'text-white' : 'text-text-secondary hover:text-text-primary border border-border'}`}
            style={active ? { backgroundColor: ACCENT } : done ? { borderColor: `${ACCENT}66`, color: ACCENT } : {}}>
            <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center text-[9px] font-mono ${
              active ? 'bg-white/25' : done ? '' : 'bg-border'}`}
              style={done ? { backgroundColor: `${ACCENT}2a` } : {}}>
              {done ? <Check size={8} weight="bold" /> : i + 1}
            </span>
            {s.label}
          </button>
        </React.Fragment>
      );
    })}
  </div>
);

const CreditField = ({ field, value, onChange }) => {
  const wide = field.type === 'textarea';
  const common = "w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary";
  return (
    <div className={wide ? 'sm:col-span-2' : ''} data-testid={`credit-app-field-${field.id}`}>
      <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">
        {field.label}{field.required && <span className="text-danger"> *</span>}
      </label>
      {field.type === 'textarea' ? (
        <textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''} className={common} />
      ) : field.type === 'select' ? (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={common}>
          <option value="">Select…</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email'
            : field.type === 'tel' ? 'tel' : field.type === 'date' ? 'date' : 'text'}
          value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''} className={common} />
      )}
      {field.help && <div className="text-[10.5px] text-text-muted mt-1 leading-snug">{field.help}</div>}
    </div>
  );
};

/* ----------------------------------------------------- submitted + chat ----- */

const SubmittedView = ({ app, past, onRefresh, onHome }) => {
  const { format } = useCurrency();
  const meta = STATUS_META[app.status] || STATUS_META.submitted;
  const amount = Number(app.amount_sought_inr || 0);

  return (
    <div className="p-4 sm:p-6" data-testid="credit-app-submitted">
      <div className="max-w-[1400px] mx-auto">
        <HomeLink onHome={onHome} />
        <div className="flex flex-col xl:flex-row gap-4 items-start">
          {/* 65 / 35, matching the services engagements page. */}
          <div className="w-full min-w-0 xl:flex-[65_1_0%] space-y-4">
            <div className="rounded-2xl border p-5 sm:p-6"
              style={{ backgroundColor: `${ACCENT}0d`, borderColor: `${ACCENT}44` }}
              data-testid="credit-app-thanks">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl inline-flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${ACCENT}1f`, color: ACCENT }}>
                  <CheckCircle size={20} weight="fill" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${TONES[meta.tone]}`}>
                      {meta.label}
                    </span>
                    {app.reference && (
                      <span className="text-[11px] font-mono text-text-muted">{app.reference}</span>
                    )}
                  </div>
                  <StartupPageTitle icon={PageHeadingIcon} className="text-xl sm:text-2xl font-heading font-bold text-text-primary leading-tight"
                    style={{ letterSpacing: '-0.01em' }}>
                    Your application is with our credit team.
                  </StartupPageTitle>
                  <p className="text-[13px] text-text-secondary leading-relaxed mt-2">
                    Thanks for bringing this to Founderboard. A reviewer is reading your numbers now and will come back
                    within <strong className="text-text-primary">{app.sla_hours || 48} hours</strong>
                    {app.response_due_at && <> — by <strong className="text-text-primary">{formatDate(app.response_due_at)}</strong></>}.
                    We&rsquo;ll take it to the lenders on our panel who actually fit your stage, so you don&rsquo;t
                    repeat this anywhere else.
                  </p>
                  <p className="text-[13px] text-text-secondary leading-relaxed mt-2">
                    Nothing more is needed from you right now. If something is unclear, or your numbers change before we
                    reply, tell us on the thread{' '}
                    <span className="xl:hidden">below</span><span className="hidden xl:inline">on the right</span> — it goes
                    straight to the reviewer handling your file.
                  </p>
                </div>
              </div>
            </div>

            {app.status === 'info_requested' && (
              <div className="rounded-xl border border-warning/40 bg-warning/10 p-3.5 flex items-start gap-2.5"
                data-testid="credit-app-info-requested">
                <ChatCircleDots size={16} weight="fill" className="text-warning flex-shrink-0 mt-0.5" />
                <div className="text-[12.5px] text-text-secondary leading-relaxed">
                  <strong className="text-text-primary">We&rsquo;ve asked you something.</strong> Check the thread —
                  your application is paused until you reply.
                </div>
              </div>
            )}

            {app.decision_note && (
              <Panel testid="credit-app-decision">
                <SectionTitle sub={`Status: ${meta.label} · ${meta.blurb}`}>Note from our credit team</SectionTitle>
                <p className="text-[13px] text-text-secondary leading-relaxed whitespace-pre-wrap">{app.decision_note}</p>
              </Panel>
            )}

            <Panel testid="credit-app-summary">
              <SectionTitle sub="What we're working from. Changes go on the thread so there's a record.">
                Your application
              </SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                <Fact label="Reference" value={app.reference || '—'} mono />
                <Fact label="Submitted" value={app.submitted_at ? formatDate(app.submitted_at) : '—'} />
                <Fact label="Amount sought" value={amount > 0 ? format(amount, 'INR') : '—'} accent />
                {(app.data?.tenure_months) && <Fact label="Tenure" value={`${app.data.tenure_months} months`} />}
                {(app.data?.legal_entity_name) && <Fact label="Entity" value={app.data.legal_entity_name} />}
                {(app.data?.entity_type) && <Fact label="Type" value={app.data.entity_type} />}
              </div>
              {(app.credit_types || []).length > 0 && (
                <div className="mt-3.5 pt-3 border-t border-border">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1.5">
                    Credit types requested ({app.credit_types.length})
                  </div>
                  <CreditTypeChips app={app} />
                </div>
              )}
              {app.consent_accepted_at && (
                <div className="mt-3 text-[11px] text-text-muted inline-flex items-center gap-1.5">
                  <ShieldCheck size={11} weight="fill" style={{ color: ACCENT }} />
                  Data-sharing consent given {formatDate(app.consent_accepted_at)} (v{app.consent?.version}).
                </div>
              )}
            </Panel>

            <div className="text-[11px] text-text-muted inline-flex items-center gap-1.5">
              <ClockCounterClockwise size={11} weight="bold" />
              Started {formatDate(app.created_at)}
              <button onClick={onRefresh} data-testid="credit-app-refresh"
                className="ml-2 underline hover:text-text-primary">Refresh</button>
            </div>

            {/* Earlier applications, if any — this one is already the hero above. */}
            <CreditHistory apps={past} />
          </div>

          <aside className="w-full min-w-0 xl:flex-[35_1_0%] xl:sticky xl:top-6" data-testid="credit-app-chat-rail">
            <div className="bg-surface border border-border rounded-xl p-4">
              {app.chat?.open ? (
                <ServiceChat
                  requestId={app.id}
                  basePath={CHAT_BASE}
                  accent={ACCENT}
                  counterpartyFallback="Founderboard Credit"
                  placeholder="Message our credit team…"
                  emptyHint={<>Ask us anything about your application — timelines, what a lender is likely to want, or
                    a change in your numbers. This goes to the reviewer handling your file, not a shared inbox.</>}
                  onRead={onRefresh}
                  compact
                />
              ) : (
                <div className="text-[12px] text-text-muted py-4 leading-relaxed">
                  The thread opens once your application is submitted.
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

/** Resolves credit_type ids to names via the form endpoint's product list. */
const CreditTypeChips = ({ app }) => {
  const [names, setNames] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get('/credit/application-form')
      .then((r) => { if (!cancelled) setNames(Object.fromEntries((r.data.credit_products || []).map((p) => [p.id, p.name]))); })
      .catch(() => { if (!cancelled) setNames({}); });
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="flex flex-wrap gap-1.5">
      {(app.credit_types || []).map((t) => (
        <span key={t} className="text-[11.5px] px-2 py-1 rounded font-medium"
          style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}>
          {names ? (names[t] || 'Credit product') : '…'}
        </span>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------- history ----- */

/**
 * (Iter 72) Past applications, the way the services Engagements tab lists
 * engagements: a tile per application with its status and a progress ring,
 * expanding to the summary and its thread.
 *
 * Before this, a declined or closed application simply vanished — the founder
 * had no record of what they'd asked for or what we'd said, and the thread went
 * with it. `progress.pct` comes from the server so the ring can't drift from the
 * status chip beside it.
 */
const CreditHistory = ({ apps, currentId, onRefresh, onOpen }) => {
  const [openId, setOpenId] = useState('');
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState('');
  // Local zeroing of the unread badge. Kept out of `detail` on purpose: writing
  // it there is what caused the admin panel's reload loop — ServiceChat calls
  // onRead on every poll, and clearing the detail unmounts the chat that fired it.
  const [read, setRead] = useState({});

  if (!apps?.length) return null;

  const toggle = async (id) => {
    if (openId === id) { setOpenId(''); setDetail(null); return; }
    setOpenId(id);
    setDetail(null);
    setBusyId(id);
    try {
      const r = await api.get(`/credit/applications/${id}`);
      setDetail(r.data.application);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open that application');
      setOpenId('');
    } finally { setBusyId(''); }
  };

  const markRead = (id) => setRead((p) => (p[id] ? p : { ...p, [id]: true }));

  return (
    <div className="mt-6" data-testid="credit-app-history">
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <h2 className="text-[15px] font-heading font-bold text-text-primary" style={{ letterSpacing: '-0.01em' }}>
          Your credit applications
          <span className="text-[12px] font-normal text-text-muted ml-2">{apps.length}</span>
        </h2>
        {onRefresh && (
          <button onClick={onRefresh} data-testid="credit-app-history-refresh"
            className="text-[11px] font-semibold text-text-muted hover:text-text-primary inline-flex items-center gap-1">
            <ArrowsClockwise size={10} weight="bold" /> Refresh
          </button>
        )}
      </div>
      <div className="space-y-2">
        {apps.map((a) => (
          <HistoryTile key={a.id} a={a} open={openId === a.id} busy={busyId === a.id}
            detail={openId === a.id ? detail : null}
            unread={read[a.id] ? 0 : (a.chat?.unread || 0)}
            // The live application opens its own screen instead of expanding —
            // that's where the form and the working thread are. Past ones expand
            // in place; there's nowhere else for them to go.
            current={a.id === currentId}
            onToggle={a.id === currentId && onOpen ? onOpen : () => toggle(a.id)}
            onRead={() => markRead(a.id)} />
        ))}
      </div>
    </div>
  );
};

const HistoryTile = ({ a, open, busy, detail, unread, current, onToggle, onRead }) => {
  const { format } = useCurrency();
  const meta = STATUS_META[a.status] || STATUS_META.submitted;
  const pct = a.progress?.pct ?? 0;
  const finished = pct >= 100;
  const amount = Number(a.amount_sought_inr || 0);

  return (
    <div className={`bg-surface border rounded-xl overflow-hidden transition-shadow ${open ? '' : 'border-border'}`}
      style={open ? { borderColor: ACCENT, boxShadow: `0 0 0 1px ${ACCENT}55, 0 10px 30px -14px ${ACCENT}80` } : undefined}
      data-testid={`credit-history-${a.id}`}>
      {/* Selection + hover come from the accent as inline custom properties.
          Tailwind's /40 surface tints resolve to near-black in the light theme. */}
      <button onClick={onToggle} data-testid={`credit-history-toggle-${a.id}`}
        style={{ '--tile-hover': `${ACCENT}14`, '--tile-open': open ? `${ACCENT}0d` : 'transparent' }}
        className="w-full text-left p-3.5 flex items-center gap-3 bg-[var(--tile-open)] hover:bg-[var(--tile-hover)] transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${TONES[meta.tone]}`}>
              {meta.label}
            </span>
            {current && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
                style={{ backgroundColor: `${ACCENT}1f`, color: ACCENT }}>Current</span>
            )}
            {a.reference && <span className="text-[11.5px] font-mono text-text-muted">{a.reference}</span>}
            {unread > 0 && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-info/15 text-info inline-flex items-center gap-1"
                data-testid={`credit-history-unread-${a.id}`}>
                <ChatCircleDots size={10} weight="fill" /> {unread}
              </span>
            )}
          </div>
          <div className="text-[13.5px] text-text-primary font-semibold mt-1">
            {a.progress?.stage_label || meta.label}
          </div>
          <div className="flex items-center gap-3 text-[11.5px] text-text-muted mt-1 flex-wrap">
            {amount > 0 && <span className="font-mono">{format(amount, a.currency || 'INR')}</span>}
            {(a.credit_types || []).length > 0 && (
              <span>{a.credit_types.length} credit type{a.credit_types.length === 1 ? '' : 's'}</span>
            )}
            <span className="inline-flex items-center gap-1">
              <ClockCounterClockwise size={10} weight="bold" />
              {a.submitted_at ? `Submitted ${formatDate(a.submitted_at)}` : `Started ${formatDate(a.created_at)}`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {a.progress?.total_steps != null && (
            <div className="text-right hidden sm:block">
              <div className="text-[12px] font-semibold text-text-primary leading-tight">
                {a.progress.done_steps} of {a.progress.total_steps}
              </div>
              <div className="text-[11px] text-text-muted leading-tight">pages done</div>
            </div>
          )}
          <ProgressDonut pct={pct} size={50} stroke={5}
            color={a.status === 'declined' || a.status === 'closed' ? '#8b8b9a' : finished ? SUCCESS : ACCENT}
            testid={`credit-history-progress-${a.id}`} />
          {current
            ? <ArrowRight size={13} weight="bold" className="text-text-muted" />
            : <CaretDown size={13} weight="bold" className="text-text-muted transition-transform"
                style={{ transform: open ? 'rotate(180deg)' : 'none' }} />}
        </div>
      </button>

      {open && (
        <div className="border-t border-border p-3.5 space-y-3" data-testid={`credit-history-detail-${a.id}`}>
          {busy && <div className="text-[12px] text-text-muted">Opening…</div>}
          {detail && (
            <>
              <div className="text-[12px] text-text-secondary">{meta.blurb}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5">
                <Fact label="Reference" value={detail.reference || '—'} mono />
                <Fact label="Submitted" value={detail.submitted_at ? formatDate(detail.submitted_at) : 'Not submitted'} />
                <Fact label="Amount sought" value={amount > 0 ? format(amount, 'INR') : '—'} accent />
                {detail.data?.legal_entity_name && <Fact label="Entity" value={detail.data.legal_entity_name} />}
                {detail.data?.tenure_months && <Fact label="Tenure" value={`${detail.data.tenure_months} months`} />}
              </div>
              {(detail.credit_types || []).length > 0 && <CreditTypeChips app={detail} />}
              {detail.decision_note && (
                <div className="rounded-lg border border-border bg-background p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">
                    Note from our credit team
                  </div>
                  <p className="text-[12.5px] text-text-secondary leading-relaxed whitespace-pre-wrap">{detail.decision_note}</p>
                </div>
              )}
              {detail.chat?.open ? (
                <ServiceChat
                  requestId={detail.id}
                  basePath={CHAT_BASE}
                  accent={ACCENT}
                  counterpartyFallback="Founderboard Credit"
                  placeholder="Message our credit team…"
                  emptyHint="Nothing was said on this application's thread."
                  onRead={onRead}
                  compact
                />
              ) : (
                <div className="text-[12px] text-text-muted">
                  This application was never submitted, so it has no thread.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------- shared ----- */

const Panel = ({ children, testid }) => (
  <div className="bg-surface border border-border rounded-2xl p-4 sm:p-5 mt-4 first:mt-0" data-testid={testid}>{children}</div>
);

const SectionTitle = ({ children, sub }) => (
  <div className="mb-3">
    <h2 className="text-[16px] font-heading font-bold text-text-primary" style={{ letterSpacing: '-0.01em' }}>{children}</h2>
    {sub && <p className="text-[12px] text-text-secondary mt-0.5 leading-snug">{sub}</p>}
  </div>
);

const Req = () => <span className="text-danger">*</span>;

const Fact = ({ label, value, accent, mono }) => (
  <div>
    <div className="text-[9.5px] uppercase tracking-wider font-bold text-text-muted">{label}</div>
    <div className={`text-[13px] font-semibold ${mono ? 'font-mono' : ''} ${accent ? '' : 'text-text-primary'}`}
      style={accent ? { color: ACCENT } : {}}>{value}</div>
  </div>
);

export default CreditApplication;
